/// <reference types="vite-plugin-pwa/client" />
/*
 * Registers the PWA service worker and owns the "new version available"
 * prompt.
 *
 * Deep-import this from an app's `main.tsx` ONLY. It imports
 * `virtual:pwa-register`, which exists only in builds that load
 * vite-plugin-pwa (admin and partner) - reaching this file from the
 * components barrel would break the sales and brandOwner builds.
 */
import { toast } from 'react-toastify';
import { registerSW } from 'virtual:pwa-register';
import { setApplyPendingUpdate, setSwRegistration } from './swRegistration';

const UPDATE_TOAST_ID = 'sw-update';
const UPDATE_CHECK_INTERVAL = 60 * 60 * 1000;
const ASKED_KEY = 'vg-sw-update-asked';
/**
 * An update notice this soon after load is workbox replaying one that was
 * already waiting when the page opened, not a version that just landed.
 */
const STARTUP_REPLAY_MS = 10 * 1000;

/**
 * Launched from the home screen or the dock rather than opened in a browser
 * tab. `navigator.standalone` is Apple's own flag, which predates the
 * display-mode query and is still how older iOS reports an installed app.
 */
const isInstalledApp = () =>
  window.matchMedia?.('(display-mode: standalone)').matches === true ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

const wasAsked = () => {
  try {
    return sessionStorage.getItem(ASKED_KEY) === '1';
  } catch {
    // Storage blocked. Asking again is the harmless way to be wrong.
    return false;
  }
};

const rememberAsked = (asked: boolean) => {
  try {
    if (asked) sessionStorage.setItem(ASKED_KEY, '1');
    else sessionStorage.removeItem(ASKED_KEY);
  } catch {
    /* Storage blocked - the prompt just keeps its old behaviour. */
  }
};

const UpdatePrompt = ({ onRefresh }: { onRefresh: () => void }) => (
  <div>
    <div style={{ marginBottom: 8 }}>A new version is available.</div>
    <button
      onClick={onRefresh}
      style={{
        background: 'var(--vg-accent, #E61E29)',
        color: '#fff',
        border: 'none',
        borderRadius: 20,
        padding: '6px 18px',
        cursor: 'pointer',
        fontWeight: 600,
      }}
    >
      Refresh
    </button>
  </div>
);

export const registerAppSw = () => {
  if (!('serviceWorker' in navigator)) return;

  /*
   * Only an installed app asks. In a browser tab the same notice reads as
   * nagging - the reader is on a web page, not in an app they launched - so
   * that case hands over silently as they leave instead (see `pagehide`).
   */
  const prompts = isInstalledApp();

  let reloading = false;
  let leaving = false;
  let missedHandover = false;
  let hadController = !!navigator.serviceWorker.controller;

  const reloadOnce = () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  };

  /*
   * Reload whenever a NEW worker takes over an already-controlled page,
   * whichever path activated it - the Refresh button here, another tab's
   * Refresh, or the 426 upgrade path. The plugin's own reload only fires
   * when workbox-window classifies the update as `isUpdate`, and updates it
   * considers "external" (found by `registration.update()` from another tab,
   * or arriving in a first-visit session) slip through that check - leaving
   * the page running old assets whose caches the new worker just cleaned up.
   *
   * The first controller change on a fresh visit is the worker CLAIMING the
   * page, not an update - swallow that one or every first visit would
   * immediately reload.
   */
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) {
      hadController = true;
      return;
    }
    // The browser-tab handover below runs while the page is already on its
    // way out; reloading into that would fight the navigation just started.
    if (leaving) {
      missedHandover = true;
      return;
    }
    reloadOnce();
  });

  window.addEventListener('pageshow', (event) => {
    leaving = false;
    // Restored from the back/forward cache under a worker that took over
    // while this page was away, so its chunks may already be swept.
    if (event.persisted && missedHandover) reloadOnce();
  });

  const updateSW = registerSW({
    onNeedRefresh() {
      if (!prompts) return;

      /*
       * Asked once per pending version. Workbox re-announces a worker that is
       * still waiting on every single page load, so without this the prompt
       * came back at every launch until it was applied. A version that lands
       * while the app is open arrives well after that startup replay, and is
       * worth asking about again.
       */
      const isStartupReplay = performance.now() < STARTUP_REPLAY_MS;
      if (isStartupReplay && wasAsked()) return;
      if (!isStartupReplay) rememberAsked(false);

      const refresh = () => {
        toast.dismiss(UPDATE_TOAST_ID);
        updateSW(true);
      };

      /*
       * Deferred a beat: a worker left waiting by a previous session fires
       * this during startup, possibly before StyledToastContainer has
       * mounted. react-toastify queues early toasts, but the defer costs
       * nothing and removes the dependency on that behaviour.
       */
      setTimeout(() => {
        toast.info(<UpdatePrompt onRefresh={refresh} />, {
          toastId: UPDATE_TOAST_ID,
          autoClose: false,
          closeOnClick: false,
          draggable: false,
          /*
           * Recorded the moment it is shown, not when it is closed:
           * react-toastify fires `onClose` only after the exit animation has
           * run, which never happens for a reader with animations turned
           * off - and "this session was already told" is the fact worth
           * keeping either way.
           */
          onOpen: () => rememberAsked(true),
        });
      }, 1000);
    },
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      setSwRegistration(registration);
      /*
       * The browser only re-checks the worker script on navigation, and an
       * SPA dashboard navigates once and then lives for days - poll hourly
       * so deploys reach long-lived tabs.
       */
      setInterval(() => {
        registration.update().catch(() => undefined);
      }, UPDATE_CHECK_INTERVAL);
    },
  });

  if (!prompts) {
    /*
     * Browser tab: hand the page over to the waiting worker as the reader
     * leaves, so their next visit starts on the new version without this one
     * ever being interrupted. Best effort by design - a reload attempt at
     * this point is ignored by the browser, and if the message does not make
     * it, the worker still takes over once every tab of the origin is closed.
     */
    window.addEventListener('pagehide', (event) => {
      /*
       * `persisted` means the page is only being frozen into the back/forward
       * cache and will be handed back intact - which on iOS is what merely
       * switching apps does. Handing over there would swap the worker under a
       * page that is coming back, and cost the reader whatever they had typed
       * when it reloaded. Only a page that is actually going away hands over.
       */
      if (event.persisted) return;
      leaving = true;
      updateSW(true);
    });
  }

  // Handed to the leaf module so the 426 upgrade path in api.ts can apply a
  // waiting update without importing the virtual module itself.
  setApplyPendingUpdate(() => updateSW(true));
};
